import React, { useState } from "react";

import "./styles/EditModal.scss";

import Modal, { ModalButton, ModalContent } from "../global/Modal";
import { Input, UploadImage, Fonts, Themes } from "../global/FormElements";
import Spinner from "../global/Spinner";
import useLocalStorage from "../../hooks/useLocalStorage";
import {
  editBusinessSheet,
  editUserData,
  getBusinessSheet,
} from "../../api/businessSheet";
import { prepareImageFile } from "../../utils/imageUpload";
import { messages } from "../../constants/messages";

// 画像の削除指示（Issue #146）: updatedData[name] === null のとき送るフラグ
const IMAGE_REMOVAL_FLAGS = {
  headerBackgroundImage: "removeHeaderBackgroundImage",
  profileImage: "removeProfileImage",
  referralSheetBackgroundImage: "removeReferralSheetBackgroundImage",
};

const toInitialData = (data) => ({
  headerBackgroundImage: data.headerBackgroundImageUrl,
  profileImage: data.profileImageUrl,
  referralSheetBackgroundImage: data.referralSheetBackgroundImageUrl,
  fontPreference: data.fontPreference,
  colorPreference: data.colorPreference,
  userName: data.userName,
  userCategory: data.userCategory,
});

const EditDesignAndImgModal = ({
  size,
  title,
  setBusinessSheetData,
  theme,
  data,
}) => {
  const [toggle, setToggle] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setValue } = useLocalStorage();

  const [updatedData, setUpdatedData] = useState(() => toInitialData(data));
  // 画像ごとの選択エラー（Issue #156）と保存 API のエラー
  const [imageErrors, setImageErrors] = useState({});
  const [saveError, setSaveError] = useState("");

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    // is file
    if (files && files.length > 0) {
      const picked = files[0];
      // 同じファイルを選び直しても change が発火するように input をクリアする
      e.target.value = "";
      const { file, error } = await prepareImageFile(picked, name);
      if (error) {
        setImageErrors((prev) => ({ ...prev, [name]: error }));
        return;
      }
      setImageErrors((prev) => ({ ...prev, [name]: "" }));
      setUpdatedData((prevState) => ({
        ...prevState,
        [name]: file,
      }));
    }
    // is string
    else {
      setUpdatedData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  // ゴミ箱: プレビューをデフォルト画像に戻す（保存するまで確定しない）
  const handleResetImage = (name) => {
    setUpdatedData((prevState) => ({
      ...prevState,
      [name]: null,
    }));
    setImageErrors((prev) => ({ ...prev, [name]: "" }));
    const input = document.querySelector(`input[name="${name}"]`);
    if (input) input.value = "";
  };

  const onToggle = () => {
    setToggle((prev) => !prev);
  };

  // 開くたびに現在の data から作り直す（前回キャンセルした削除・選択を持ち越さない）
  const onOpen = () => {
    setUpdatedData(toInitialData(data));
    setImageErrors({});
    setSaveError("");
    setToggle(true);
  };

  const onEdit = async () => {
    setLoading(true);
    setSaveError("");

    const userBussinessData = {
      fontPreference: updatedData.fontPreference,
      colorPreference: updatedData.colorPreference,
    };
    // 画像は File のときだけ送る（未変更の URL 文字列は送らない）。
    // ゴミ箱で null にした枠は削除フラグを送る（axios は null を FormData に入れないため）
    Object.entries(IMAGE_REMOVAL_FLAGS).forEach(([name, flag]) => {
      const value = updatedData[name];
      userBussinessData[name] = value instanceof Blob ? value : undefined;
      if (value === null) {
        userBussinessData[flag] = true;
      }
    });

    const userData = {
      name: updatedData.userName,
      category: updatedData.userCategory,
    };

    try {
      const result = await editBusinessSheet(userBussinessData);
      // handleApiResponse は HTTP エラーでも例外を投げず { status } を返す
      if (result?.status >= 400) {
        setSaveError(
          result.status === 413 ? messages.imageTooLarge : messages.tryAgain,
        );
        setLoading(false);
        return;
      }
      await editUserData(userData);
    } catch (error) {
      console.error("Failed to update business sheet:", error);
      setSaveError(messages.tryAgain);
      setLoading(false);
      return;
    }

    // 保存後はサーバーの値を再取得して表示を更新する（画像 URL はサーバー側で決まる）
    let next;
    try {
      const response = await getBusinessSheet();
      if (response?.status === 200 && response.data) {
        next = response.data;
      }
    } catch (error) {
      console.error("Failed to refetch business sheet:", error);
    }

    if (next) {
      // useBusiness と同じ保存の仕方に揃える
      setValue("profileImageUrl", next.profileImageUrl || "");
      setValue("businessSheetData", next);
    } else {
      // 再取得に失敗: 画像 URL は変更前のまま、文字系のみ反映する
      next = {
        ...data,
        fontPreference: updatedData.fontPreference,
        colorPreference: updatedData.colorPreference,
        userName: updatedData.userName,
        userCategory: updatedData.userCategory,
      };
      setValue("businessSheetData", next);
    }

    setBusinessSheetData(next);
    onToggle();
    setLoading(false);
  };

  return (
    <Modal>
      <ModalButton>
        <div className="editProfile" onClick={onOpen}>
          <img src="/images/edit.png" alt="copy content" />
          <span>プロフィールを編集</span>
        </div>
      </ModalButton>
      <ModalContent toggle={toggle} onToggle={onToggle} size={size}>
        <div className="editModal">
          <div className="title">{title}</div>
          <div className="children">
            <UploadImage
              title="ヘッダー背景画像を設定してください"
              value={updatedData?.headerBackgroundImage}
              onChange={handleChange}
              name="headerBackgroundImage"
              resetImage={() => handleResetImage("headerBackgroundImage")}
              error={imageErrors.headerBackgroundImage}
            />
            <UploadImage
              title="プロフィール画像を設定してください"
              value={updatedData?.profileImage}
              onChange={handleChange}
              name="profileImage"
              resetImage={() => handleResetImage("profileImage")}
              error={imageErrors.profileImage}
            />

            <Input
              lable="氏名"
              placeholder="氏名"
              maxLength={20}
              value={updatedData.userName}
              onChange={handleChange}
              name="userName"
            />

            <Input
              lable="カテゴリー"
              placeholder="カテゴリー"
              maxLength={20}
              value={updatedData.userCategory}
              onChange={handleChange}
              name="userCategory"
            />

            <UploadImage
              title="リファーラルシートの背景画像をアップロードしてください"
              value={updatedData?.referralSheetBackgroundImage}
              onChange={handleChange}
              name="referralSheetBackgroundImage"
              resetImage={() =>
                handleResetImage("referralSheetBackgroundImage")
              }
              error={imageErrors.referralSheetBackgroundImage}
            />
            <Fonts
              title="文字のタイプを選んでください"
              value={updatedData?.fontPreference}
              onChange={handleChange}
              name="fontPreference"
            />
            <Themes
              title="色を選んでください"
              value={updatedData?.colorPreference}
              onChange={handleChange}
              name="colorPreference"
            />
          </div>
          {saveError && (
            <p className="upload-image-error" role="alert">
              {saveError}
            </p>
          )}
          <div className="controlButtons">
            <button className="cancel" onClick={onToggle}>
              キャンセル
            </button>
            <button
              disabled={
                !updatedData.userName.trim() || !updatedData.userCategory.trim()
              }
              type="button"
              className="update"
              style={{
                backgroundColor: theme ? theme : "#999999",
              }}
              onClick={onEdit}
            >
              更新する
              {loading && <Spinner />}
            </button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default EditDesignAndImgModal;
